import axiosRaw from 'axios';
import bs58 from 'bs58';
const axios = axiosRaw.create({ timeout: 60000 });
import { getSymmetricKey, decryptToAsciiString } from './mobileEncryption';
import { completeFreshWalletBoundAuthorization } from '../wallet/connectionOperationGuard';
import { invalidateRefreshBinding } from './authRefreshManager';
import { getAuthDiagnostic } from './authDiagnostics';
const baseUrl = process.env.DWINITY_BACKEND ?? "https://api.dev.dwinity.com";
const apiVersion = process.env.API_VERSION || "1.0";
const mpcClientProxyUrl = "https://mpc-client.portalhq.io";
function encodeToBase58(plainText) {
    const sendBytes = new TextEncoder().encode(plainText);
    return bs58.encode(sendBytes);
}
export function generateExpirationTimeStamp() {
    const currentTimestamp = Math.floor(Date.now() / 1000);
    return currentTimestamp + 600;
}
const preSignedCache = new Map();
export function setPreSignedForEndpoint(endpoint, fullMessage, signature, expirationTimestamp) {
    preSignedCache.set(endpoint, { fullMessage, signature, timestamp: expirationTimestamp, createdAt: Date.now() });
    console.log('[MPC] Pre-signed signature cached for endpoint:', getAuthDiagnostic(undefined, endpoint).endpoint);
}
export function clearPreSignedCache() {
    preSignedCache.clear();
}
export function buildEndpointMessage(walletAddress, sendObject, timestamp) {
    const fullSendObject = {
        ...sendObject,
        expirationTimestamp: timestamp,
        ncWalletAddress: walletAddress,
    };
    return JSON.stringify(fullSendObject);
}
function toHexString(message) {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(message);
    const hex = Array.from(bytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    return `0x${hex}`;
}
const STORAGE_KEYS = {
    CLIENT_ID: 'mobile_mpc_client_id',
    CLIENT_SESSION_TOKEN: 'mobile_mpc_session_token',
    USER_SHARES: 'mobile_mpc_user_shares',
    JWT: 'jwt',
    REFRESH_TOKEN: 'mobile_refresh_token',
    WALLET_ADDRESS: 'mobile_auth_wallet_address',
    MPC_PROGRESS: 'mobile_mpc_progress',
};
const MPC_PROGRESS_TTL_MS = 10 * 60 * 1000;
export function saveMpcProgress(step, walletAddress, data = {}) {
    const state = { step, walletAddress: walletAddress.toLowerCase(), data, timestamp: Date.now() };
    sessionStorage.setItem(STORAGE_KEYS.MPC_PROGRESS, JSON.stringify(state));
    console.log('[MPC] Progress saved');
}
export function loadMpcProgress() {
    const raw = sessionStorage.getItem(STORAGE_KEYS.MPC_PROGRESS);
    if (!raw)
        return null;
    try {
        const state = JSON.parse(raw);
        if (Date.now() - state.timestamp > MPC_PROGRESS_TTL_MS) {
            console.log('[MPC] Saved progress expired, clearing');
            clearMpcProgress();
            return null;
        }
        return state;
    }
    catch {
        clearMpcProgress();
        return null;
    }
}
export function clearMpcProgress() {
    sessionStorage.removeItem(STORAGE_KEYS.MPC_PROGRESS);
}
export function hasInterruptedProgress(walletAddress) {
    const loadedProgress = loadMpcProgress();
    const progress = loadedProgress?.walletAddress === walletAddress.toLowerCase()
        ? loadedProgress
        : null;
    if (!progress)
        return false;
    return progress.walletAddress === walletAddress.toLowerCase();
}
export function storeClientId(id) { localStorage.setItem(STORAGE_KEYS.CLIENT_ID, id); }
export function loadClientId() { return localStorage.getItem(STORAGE_KEYS.CLIENT_ID); }
export function storeClientSessionToken(t) { localStorage.setItem(STORAGE_KEYS.CLIENT_SESSION_TOKEN, t); }
export function loadClientSessionToken() { return localStorage.getItem(STORAGE_KEYS.CLIENT_SESSION_TOKEN); }
export function storeUserShares(s) { localStorage.setItem(STORAGE_KEYS.USER_SHARES, s); }
export function loadUserShares() { return localStorage.getItem(STORAGE_KEYS.USER_SHARES); }
export function storeJwt(jwt) { localStorage.setItem(STORAGE_KEYS.JWT, jwt); }
export function loadJwt() { return localStorage.getItem(STORAGE_KEYS.JWT); }
export function storeRefreshToken(token) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
    invalidateRefreshBinding();
}
export function loadRefreshToken() { return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN); }
export function storeWalletAddress(addr) {
    localStorage.setItem(STORAGE_KEYS.WALLET_ADDRESS, addr.toLowerCase());
    invalidateRefreshBinding();
}
export function loadWalletAddress() { return localStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS); }
export function storeMpcWalletData(data) {
    if (data.clientId)
        storeClientId(data.clientId);
    if (data.clientSessionToken)
        storeClientSessionToken(data.clientSessionToken);
    if (data.userSharesString)
        storeUserShares(data.userSharesString);
    if (data.authToken)
        storeJwt(data.authToken);
}
export function hasCachedMpcData() {
    return !!(loadClientId() && loadUserShares());
}
export function clearAllMobileAuthData() {
    invalidateRefreshBinding();
    const persistentKeys = [STORAGE_KEYS.CLIENT_ID, STORAGE_KEYS.CLIENT_SESSION_TOKEN, STORAGE_KEYS.USER_SHARES, STORAGE_KEYS.JWT, STORAGE_KEYS.REFRESH_TOKEN, STORAGE_KEYS.WALLET_ADDRESS];
    persistentKeys.forEach(k => localStorage.removeItem(k));
    sessionStorage.removeItem(STORAGE_KEYS.MPC_PROGRESS);
}
export async function signMessage(signMessageAsync, message) {
    const signature = await signMessageAsync({ message });
    return signature.startsWith('0x') ? signature.substring(2) : signature;
}
async function sendMessageWithNcSignature(signMessageAsync, walletAddress, sendObject) {
    const method = sendObject.method || 'get';
    const endpoint = sendObject.endpoint;
    let sendString;
    let signature;
    const cached = preSignedCache.get(endpoint);
    const currentTimeSec = Math.floor(Date.now() / 1000);
    const isCacheValid = cached && (cached.timestamp - currentTimeSec) > 60;
    if (isCacheValid) {
        console.log('[MPC] sendMessageWithNcSignature: using PRE-SIGNED signature for', getAuthDiagnostic(undefined, endpoint).endpoint);
        preSignedCache.delete(endpoint);
        sendString = cached.fullMessage;
        signature = cached.signature;
        if (signature.startsWith('0x')) {
            signature = signature.substring(2);
        }
    }
    else {
        if (cached) {
            console.log('[MPC] sendMessageWithNcSignature: pre-signed cache EXPIRED for', getAuthDiagnostic(undefined, endpoint).endpoint);
            preSignedCache.delete(endpoint);
        }
        const expirationTimestamp = generateExpirationTimeStamp();
        const fullSendObject = {
            ...sendObject,
            expirationTimestamp,
            ncWalletAddress: walletAddress,
        };
        sendString = JSON.stringify(fullSendObject);
        console.log('[MPC] sendMessageWithNcSignature: requesting signature for', getAuthDiagnostic(undefined, endpoint).endpoint);
        try {
            signature = await signMessage(signMessageAsync, sendString);
            console.log('[MPC] sendMessageWithNcSignature: signature obtained for', getAuthDiagnostic(undefined, endpoint).endpoint);
        }
        catch (e) {
            console.error('[MPC] sendMessageWithNcSignature: SIGNING FAILED', getAuthDiagnostic(e, endpoint));
            throw e;
        }
    }
    const sendBytesEncoded = encodeToBase58(sendString);
    const url = baseUrl + endpoint;
    const params = {
        message: sendBytesEncoded,
        signature: signature,
    };
    console.log('[MPC] sendMessageWithNcSignature: calling backend', getAuthDiagnostic(undefined, endpoint).endpoint);
    const MAX_BACKEND_RETRIES = 3;
    let lastError = null;
    for (let backendAttempt = 1; backendAttempt <= MAX_BACKEND_RETRIES; backendAttempt++) {
        try {
            const response = method === 'post'
                ? await axios.post(url, '', { params })
                : await axios.get(url, { params });
            console.log('[MPC] sendMessageWithNcSignature: backend responded OK for', getAuthDiagnostic(undefined, endpoint).endpoint);
            return response.data;
        }
        catch (e) {
            lastError = e;
            const status = e?.response?.status;
            const isNetworkError = !e?.response && (e?.code === 'ECONNABORTED' || e?.code === 'ERR_NETWORK' || e?.message?.includes('Network Error') || e?.message?.includes('timeout'));
            const isRetryable = status === 503 || status === 502 || status === 504 || isNetworkError;
            console.error('[MPC] sendMessageWithNcSignature: BACKEND CALL FAILED', getAuthDiagnostic(e, endpoint));
            if (isRetryable && backendAttempt < MAX_BACKEND_RETRIES) {
                const delayMs = 1000 * backendAttempt;
                console.log('[MPC] Retrying backend call...');
                await new Promise(r => setTimeout(r, delayMs));
                continue;
            }
            throw e;
        }
    }
    throw lastError;
}
export async function createMpcClient(signMessageAsync, walletAddress) {
    const postObject = {
        method: 'post',
        endpoint: `/api/${apiVersion}/mpc/client`,
        ncwa: walletAddress,
    };
    const result = await sendMessageWithNcSignature(signMessageAsync, walletAddress, postObject);
    return result;
}
export async function generateMpcWalletUserShare(clientSessionToken) {
    const response = await axios.post(`${mpcClientProxyUrl}/v1/generate`, '{}', {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${clientSessionToken}`,
        },
    });
    return response.data;
}
export async function backupUserShares(clientSessionToken, userShares) {
    const response = await axios.post(`${mpcClientProxyUrl}/v1/backup`, {
        generateResponse: JSON.stringify(userShares),
    }, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${clientSessionToken}`,
        },
    });
    return response.data;
}
export async function recoverUserShares(clientSessionToken, backupShares) {
    const response = await axios.post(`${mpcClientProxyUrl}/v1/recover`, {
        backupResponse: JSON.stringify(backupShares),
    }, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${clientSessionToken}`,
        },
    });
    return response.data;
}
export async function notifyMpcWalletOnUserSharesStored(clientSessionToken, secp256k1ShareId, ed25519ShareId) {
    await axios.patch(`${baseUrl}/api/${apiVersion}/mpc/signing-share-pairs`, {
        status: 'STORED_CLIENT',
        signingSharePairIds: [secp256k1ShareId, ed25519ShareId],
    }, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${clientSessionToken}`,
        },
    });
}
export async function notifyMpcWalletOnBackupSharesStored(clientSessionToken, secp256k1ShareId, ed25519ShareId) {
    await axios.patch(`${baseUrl}/api/${apiVersion}/mpc/backup-share-pairs`, {
        status: 'STORED_CLIENT_BACKUP_SHARE',
        backupSharePairIds: [secp256k1ShareId, ed25519ShareId],
    }, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${clientSessionToken}`,
        },
    });
}
export async function refreshClientSessionTokenByRecoveryToken(recoveryAuthToken, mpcClientId) {
    const messageObject = { clientId: mpcClientId };
    const message = encodeToBase58(JSON.stringify(messageObject));
    const response = await axios.post(`${baseUrl}/api/${apiVersion}/mpc/refresh-using-token`, '', {
        params: { message },
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${recoveryAuthToken}`,
        },
    });
    return response.data;
}
export async function isClientSessionTokenValid(clientSessionToken) {
    try {
        const response = await axios.get(`${baseUrl}/api/${apiVersion}/mpc/is-session-token-valid`, {
            params: { clientSessionToken },
            headers: { 'Content-Type': 'application/json' },
        });
        return response.data.isValid;
    }
    catch {
        return false;
    }
}
export async function refreshClientSessionToken(signMessageAsync, walletAddress, mpcClientId) {
    const postObject = {
        method: 'post',
        endpoint: `/api/${apiVersion}/mpc/refresh`,
        ncwa: walletAddress,
        clientId: mpcClientId,
    };
    const result = await sendMessageWithNcSignature(signMessageAsync, walletAddress, postObject);
    return result;
}
async function mpcSign(clientSessionToken, userShare, message) {
    const hexMessage = toHexString(message);
    console.log('[MPC] mpcSign: calling', getAuthDiagnostic(undefined, '/v1/raw/sign/ED25519').endpoint);
    const MAX_SIGN_RETRIES = 3;
    let lastError = null;
    for (let signAttempt = 1; signAttempt <= MAX_SIGN_RETRIES; signAttempt++) {
        try {
            const response = await axios.post(`${mpcClientProxyUrl}/v1/raw/sign/ED25519`, {
                param: hexMessage.substring(2),
                share: userShare,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${clientSessionToken}`,
                },
            });
            console.log('[MPC] mpcSign: signature received');
            return response.data.data;
        }
        catch (e) {
            lastError = e;
            const status = e?.response?.status;
            const isRetryable = status === 503 || status === 502 || status === 504;
            console.error('[MPC] mpcSign FAILED', getAuthDiagnostic(e, '/v1/raw/sign/ED25519'));
            if (isRetryable && signAttempt < MAX_SIGN_RETRIES) {
                await new Promise(r => setTimeout(r, 1000 * signAttempt));
                continue;
            }
            throw e;
        }
    }
    throw lastError;
}
async function sendMessageWithMpcWallet(clientSessionToken, userShare, sendObject) {
    const method = sendObject.method || 'get';
    const expirationTimestamp = generateExpirationTimeStamp();
    const fullSendObject = {
        ...sendObject,
        expirationTimestamp,
    };
    const sendString = JSON.stringify(fullSendObject);
    const sendBytesEncoded = encodeToBase58(sendString);
    const signature = await mpcSign(clientSessionToken, userShare, sendString);
    const url = baseUrl + sendObject.endpoint;
    const params = {
        message: sendBytesEncoded,
        signature: signature,
    };
    const MAX_MPC_RETRIES = 3;
    let lastError = null;
    for (let mpcAttempt = 1; mpcAttempt <= MAX_MPC_RETRIES; mpcAttempt++) {
        try {
            const response = method === 'post'
                ? await axios.post(url, '', { params })
                : await axios.get(url, { params });
            return response.data;
        }
        catch (e) {
            lastError = e;
            const status = e?.response?.status;
            const isRetryable = status === 503 || status === 502 || status === 504;
            console.error('[MPC] sendMessageWithMpcWallet: BACKEND CALL FAILED', getAuthDiagnostic(e, sendObject.endpoint));
            if (isRetryable && mpcAttempt < MAX_MPC_RETRIES) {
                await new Promise(r => setTimeout(r, 1000 * mpcAttempt));
                continue;
            }
            throw e;
        }
    }
    throw lastError;
}
export async function createAuthToken(clientId, clientSessionToken, userSharesString, assertCurrent = () => { }) {
    const sendObject = {
        method: 'get',
        endpoint: `/api/${apiVersion}/auth/token`,
        clientId: clientId,
    };
    const multiUserShares = JSON.parse(userSharesString);
    try {
        console.log('[MPC] createAuthToken: starting MPC sign with ED25519 share');
        const response = await sendMessageWithMpcWallet(clientSessionToken, multiUserShares.ED25519.share, sendObject);
        assertCurrent();
        if (response?.refreshToken) {
            storeRefreshToken(response.refreshToken);
        }
        return response.authToken;
    }
    catch (error) {
        const axiosError = error;
        console.error('[MPC] createAuthToken failed', getAuthDiagnostic(error, sendObject.endpoint));
        const status = axiosError?.response?.status;
        if (status === 500 || status === 503 || status === 502 || status === 504) {
            return undefined;
        }
        throw error;
    }
}
export async function retrieveBackupSharesFromBackend(signMessageAsync, walletAddress) {
    const postObject = {
        method: 'get',
        endpoint: `/api/${apiVersion}/mpc/recover`,
        ncwa: walletAddress,
    };
    try {
        const result = await sendMessageWithNcSignature(signMessageAsync, walletAddress, postObject);
        return result;
    }
    catch (error) {
        const axiosError = error;
        if (axiosError?.response?.data?.details?.includes('object not found')) {
            return null;
        }
        throw error;
    }
}
export async function createMpcWalletProcess(signMessageAsync, walletAddress, assertCurrent = () => { }) {
    assertCurrent();
    console.log('[MPC] Creating new MPC wallet...');
    const createMpcClientResp = await createMpcClient(signMessageAsync, walletAddress);
    assertCurrent();
    console.log('[MPC] Client created');
    const clientId = createMpcClientResp.clientId;
    const clientSessionToken = createMpcClientResp.clientSessionToken;
    const jwt = createMpcClientResp.authToken;
    storeClientId(clientId);
    storeClientSessionToken(clientSessionToken);
    saveMpcProgress('client_created', walletAddress, {
        clientId,
        clientSessionToken,
        authToken: jwt,
    });
    const userShares = await generateMpcWalletUserShare(clientSessionToken);
    assertCurrent();
    console.log('[MPC] User shares generated');
    const userSharesString = JSON.stringify(userShares);
    storeUserShares(userSharesString);
    saveMpcProgress('shares_generated', walletAddress, {
        clientId,
        clientSessionToken,
        authToken: jwt,
        userSharesString,
    });
    await notifyMpcWalletOnUserSharesStored(clientSessionToken, userShares.SECP256K1.id, userShares.ED25519.id);
    assertCurrent();
    console.log('[MPC] User shares stored notification sent');
    const backupShares = await backupUserShares(clientSessionToken, userShares);
    assertCurrent();
    console.log('[MPC] Backup shares created');
    await notifyMpcWalletOnBackupSharesStored(clientSessionToken, backupShares.SECP256K1.id, backupShares.ED25519.id);
    assertCurrent();
    console.log('[MPC] Backup shares stored notification sent');
    // The create response token is recovery/onboarding input only. A dashboard
    // JWT is persisted only after the common signed refresh + auth issuance.
    clearMpcProgress();
    try {
        console.log('[MPC] Pre-requesting symmetric key for future recovery logins...');
        await getSymmetricKey(signMessageAsync, walletAddress, assertCurrent);
        assertCurrent();
        console.log('[MPC] Symmetric key signature saved for future use');
    }
    catch (e) {
        assertCurrent();
        console.warn('[MPC] Failed to pre-request symmetric key (non-critical)', getAuthDiagnostic(e));
    }
    return {
        clientId,
        clientSessionToken,
        userSharesString,
        ethWalletAddress: null,
        solanaWalletAddress: null,
        authToken: jwt,
    };
}
const DEBUG_LOG_STORAGE_KEY = 'mobile_debug_log_v2';
// Only append entries produced in this module, never untrusted persisted logs.
const safeDebugLogs = [];
function debugLog(msg, diagnostic) {
    if (diagnostic)
        console.log(msg, diagnostic);
    else
        console.log(msg);
    try {
        sessionStorage.removeItem('mobile_debug_log');
        safeDebugLogs.push(`${new Date().toLocaleTimeString()}: ${msg}${diagnostic ? ` ${JSON.stringify(diagnostic)}` : ''}`);
        if (safeDebugLogs.length > 30)
            safeDebugLogs.splice(0, safeDebugLogs.length - 30);
        sessionStorage.setItem(DEBUG_LOG_STORAGE_KEY, JSON.stringify(safeDebugLogs));
    }
    catch { }
}
export async function recoverMpcWalletProcess(signMessageAsync, walletAddress, assertCurrent = () => { }) {
    assertCurrent();
    debugLog('[MPC] Attempting to recover existing MPC wallet...');
    const loadedProgress = loadMpcProgress();
    const progress = loadedProgress?.walletAddress === walletAddress.toLowerCase()
        ? loadedProgress
        : null;
    let backupSharesResp = null;
    let symmetricKeyObtained = false;
    if (progress && progress.step === 'backup_shares_retrieved' && progress.data.backupSharesResp) {
        debugLog('[MPC] Resuming: backup_shares_retrieved');
        backupSharesResp = progress.data.backupSharesResp;
        symmetricKeyObtained = true;
    }
    else if (progress && progress.step === 'symmetric_key_obtained') {
        debugLog('[MPC] Resuming: symmetric_key_obtained');
        symmetricKeyObtained = true;
    }
    if (!symmetricKeyObtained) {
        debugLog('[MPC] SIG1: Getting symmetric key...');
        await getSymmetricKey(signMessageAsync, walletAddress, assertCurrent);
        assertCurrent();
        saveMpcProgress('symmetric_key_obtained', walletAddress, {});
        debugLog('[MPC] SIG1: Done, key saved');
    }
    if (!backupSharesResp) {
        debugLog('[MPC] SIG2: Retrieving backup shares...');
        backupSharesResp = await retrieveBackupSharesFromBackend(signMessageAsync, walletAddress);
        assertCurrent();
        if (!backupSharesResp) {
            debugLog('[MPC] No existing MPC wallet found');
            return null;
        }
        saveMpcProgress('backup_shares_retrieved', walletAddress, {
            backupSharesResp: backupSharesResp,
        });
        debugLog('[MPC] SIG2: Done, backup shares saved');
    }
    debugLog('[MPC] STEP 1/7: Re-deriving symmetric key...');
    let symmetricKey;
    try {
        symmetricKey = await getSymmetricKey(signMessageAsync, walletAddress, assertCurrent);
        assertCurrent();
        debugLog('[MPC] STEP 1/7 OK');
    }
    catch (e) {
        debugLog('[MPC] STEP 1/7 FAIL', getAuthDiagnostic(e));
        throw e;
    }
    debugLog('[MPC] STEP 2/7: Decrypting...');
    let receivedClientId;
    let receivedBackupSharesString;
    let receivedEthWalletAddress;
    let receivedSolanaWalletAddress;
    try {
        receivedClientId = await decryptToAsciiString(symmetricKey, backupSharesResp.backupInfo.encClientId);
        receivedBackupSharesString = await decryptToAsciiString(symmetricKey, backupSharesResp.backupInfo.encBackupShares);
        receivedEthWalletAddress = backupSharesResp.backupInfo.encEthWalletAddress
            ? await decryptToAsciiString(symmetricKey, backupSharesResp.backupInfo.encEthWalletAddress)
            : null;
        receivedSolanaWalletAddress = backupSharesResp.backupInfo.encSolanaWalletAddress
            ? await decryptToAsciiString(symmetricKey, backupSharesResp.backupInfo.encSolanaWalletAddress)
            : null;
        assertCurrent();
        debugLog('[MPC] STEP 2/7 OK');
    }
    catch (e) {
        assertCurrent();
        debugLog('[MPC] STEP 2/7 FAIL', getAuthDiagnostic(e));
        debugLog('[MPC] Clearing stale data and retrying with fresh signatures...');
        clearMpcProgress();
        localStorage.removeItem('mobile_userKey_signature');
        debugLog('[MPC] RETRY: Requesting fresh symmetric key (new signature)...');
        const freshKey = await getSymmetricKey(signMessageAsync, walletAddress, assertCurrent);
        assertCurrent();
        saveMpcProgress('symmetric_key_obtained', walletAddress, {});
        debugLog('[MPC] RETRY: Fresh key obtained, fetching fresh backup shares...');
        const freshBackup = await retrieveBackupSharesFromBackend(signMessageAsync, walletAddress);
        assertCurrent();
        if (!freshBackup) {
            debugLog('[MPC] RETRY: No backup found on server');
            return null;
        }
        debugLog('[MPC] RETRY: Fresh backup received, decrypting...');
        try {
            receivedClientId = await decryptToAsciiString(freshKey, freshBackup.backupInfo.encClientId);
            receivedBackupSharesString = await decryptToAsciiString(freshKey, freshBackup.backupInfo.encBackupShares);
            receivedEthWalletAddress = freshBackup.backupInfo.encEthWalletAddress
                ? await decryptToAsciiString(freshKey, freshBackup.backupInfo.encEthWalletAddress)
                : null;
            receivedSolanaWalletAddress = freshBackup.backupInfo.encSolanaWalletAddress
                ? await decryptToAsciiString(freshKey, freshBackup.backupInfo.encSolanaWalletAddress)
                : null;
            assertCurrent();
            backupSharesResp = freshBackup;
            symmetricKey = freshKey;
            debugLog('[MPC] RETRY: Decryption OK!');
        }
        catch (e2) {
            debugLog('[MPC] RETRY FAIL', getAuthDiagnostic(e2));
            throw e2;
        }
    }
    const recoveryAuthToken = backupSharesResp.authToken;
    storeClientId(receivedClientId);
    saveMpcProgress('decryption_complete', walletAddress, {
        clientId: receivedClientId,
        recoveryAuthToken,
        backupSharesString: receivedBackupSharesString,
        ethWalletAddress: receivedEthWalletAddress,
        solanaWalletAddress: receivedSolanaWalletAddress,
    });
    debugLog('[MPC] STEP 3/7: Refreshing session token...');
    let clientSessionToken;
    try {
        const clientSessionTokenHolder = await refreshClientSessionTokenByRecoveryToken(recoveryAuthToken, receivedClientId);
        assertCurrent();
        clientSessionToken = clientSessionTokenHolder.clientSessionToken;
        storeClientSessionToken(clientSessionToken);
        debugLog('[MPC] STEP 3/7 OK');
    }
    catch (e) {
        debugLog('[MPC] STEP 3/7 FAIL', getAuthDiagnostic(e, `/api/${apiVersion}/mpc/refresh-using-token`));
        throw e;
    }
    debugLog('[MPC] STEP 4/7: Recovering user shares...');
    let userShares;
    try {
        const receivedBackupSharesObj = JSON.parse(receivedBackupSharesString);
        userShares = await recoverUserShares(clientSessionToken, receivedBackupSharesObj);
        assertCurrent();
        debugLog('[MPC] STEP 4/7 OK');
    }
    catch (e) {
        debugLog('[MPC] STEP 4/7 FAIL', getAuthDiagnostic(e, '/v1/recover'));
        throw e;
    }
    const userSharesString = JSON.stringify(userShares);
    storeUserShares(userSharesString);
    debugLog('[MPC] STEP 5/7: Notifying server...');
    try {
        await notifyMpcWalletOnUserSharesStored(clientSessionToken, userShares.SECP256K1.id, userShares.ED25519.id);
        assertCurrent();
        debugLog('[MPC] STEP 5/7 OK');
    }
    catch (e) {
        debugLog('[MPC] STEP 5/7 FAIL', getAuthDiagnostic(e, `/api/${apiVersion}/mpc/signing-share-pairs`));
        throw e;
    }
    debugLog('[MPC] STEP 6/7: Waiting 2s...');
    await new Promise(r => setTimeout(r, 2000));
    assertCurrent();
    debugLog('[MPC] STEP 6/7 OK');
    clearMpcProgress();
    debugLog('[MPC] STEP 7/7: Recovery complete!');
    return {
        clientId: receivedClientId,
        clientSessionToken,
        userSharesString,
        ethWalletAddress: receivedEthWalletAddress,
        solanaWalletAddress: receivedSolanaWalletAddress,
        authToken: recoveryAuthToken,
    };
}
export async function createAuthTokenProcess(signMessageAsync, walletAddress, mpcWalletData, assertCurrent = () => { }) {
    const { clientId, userSharesString } = mpcWalletData;
    let { clientSessionToken } = mpcWalletData;
    if (!clientId || !userSharesString) {
        console.log('[MPC] Missing client data, cannot create auth token');
        return mpcWalletData;
    }
    const MAX_RETRIES = 3;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        console.log(`[MPC] Creating auth token (attempt ${attempt}/${MAX_RETRIES})...`);
        try {
            if (!clientSessionToken) {
                console.log('[MPC] No session token, refreshing with recovery token...');
                if (mpcWalletData.authToken) {
                    try {
                        const tokenHolder = await refreshClientSessionTokenByRecoveryToken(mpcWalletData.authToken, clientId);
                        assertCurrent();
                        clientSessionToken = tokenHolder.clientSessionToken;
                        storeClientSessionToken(clientSessionToken);
                        mpcWalletData.clientSessionToken = clientSessionToken;
                        console.log('[MPC] Session token refreshed via recovery token (no wallet signature needed)');
                    }
                    catch (refreshErr) {
                        assertCurrent();
                        console.warn('[MPC] Recovery token refresh failed, trying wallet signature...');
                        const tokenHolder = await refreshClientSessionToken(signMessageAsync, walletAddress, clientId);
                        assertCurrent();
                        clientSessionToken = tokenHolder.clientSessionToken;
                        storeClientSessionToken(clientSessionToken);
                        mpcWalletData.clientSessionToken = clientSessionToken;
                        console.log('[MPC] Session token refreshed via wallet signature');
                    }
                }
                else {
                    console.log('[MPC] No recovery token available, refreshing via wallet signature...');
                    const tokenHolder = await refreshClientSessionToken(signMessageAsync, walletAddress, clientId);
                    assertCurrent();
                    clientSessionToken = tokenHolder.clientSessionToken;
                    storeClientSessionToken(clientSessionToken);
                    mpcWalletData.clientSessionToken = clientSessionToken;
                    console.log('[MPC] Session token refreshed via wallet signature');
                }
            }
            const jwt = await createAuthToken(clientId, clientSessionToken, userSharesString, assertCurrent);
            assertCurrent();
            if (jwt) {
                assertCurrent();
                storeJwt(jwt);
                mpcWalletData.authToken = jwt;
                console.log('[MPC] Auth token created successfully');
                return mpcWalletData;
            }
            else {
                console.warn(`[MPC] createAuthToken returned undefined on attempt ${attempt} (likely server error)`);
                if (attempt >= 2) {
                    clientSessionToken = null;
                }
                if (attempt < MAX_RETRIES) {
                    console.log('[MPC] Will retry', attempt >= 2 ? 'with fresh session token...' : 'with same session token...');
                    await new Promise(r => setTimeout(r, 1000 * attempt));
                }
            }
        }
        catch (error) {
            // Do not retry or mutate credentials for an account/session that changed
            // while refresh or token issuance was in flight.
            assertCurrent();
            console.error('[MPC] createAuthToken attempt failed', getAuthDiagnostic(error));
            const status = error?.response?.status;
            // Authorization denials are final. Do not turn them into a recovery
            // token fallback or retry them as if the backend were unavailable.
            if (status === 401 || status === 403) {
                throw error;
            }
            if (attempt >= 2) {
                clientSessionToken = null;
            }
            if (attempt < MAX_RETRIES) {
                console.log('[MPC] Will retry', attempt >= 2 ? 'with fresh session token...' : 'with same session token...');
                await new Promise(r => setTimeout(r, 1000 * attempt));
                assertCurrent();
            }
        }
    }
    // A recovery token proves only recovery capability. It is not a dashboard
    // JWT and must never unlock routes without a successful backend auth issue.
    console.warn('[MPC] All createAuthToken attempts failed; no dashboard JWT issued');
    mpcWalletData.authToken = null;
    return mpcWalletData;
}
export async function resumeFromDecryptionComplete(signMessageAsync, walletAddress, progress, assertCurrent = () => { }) {
    console.log('[MPC] Resuming from decryption_complete step...');
    assertCurrent();
    const { clientId, recoveryAuthToken, backupSharesString, ethWalletAddress, solanaWalletAddress, } = progress.data;
    storeClientId(clientId);
    console.log('[MPC] Refreshing client session token with recovery token...');
    const clientSessionTokenHolder = await refreshClientSessionTokenByRecoveryToken(recoveryAuthToken, clientId);
    assertCurrent();
    const clientSessionToken = clientSessionTokenHolder.clientSessionToken;
    storeClientSessionToken(clientSessionToken);
    console.log('[MPC] Client session token obtained');
    const backupSharesObj = JSON.parse(backupSharesString);
    console.log('[MPC] Recovering user shares from backup...');
    const userShares = await recoverUserShares(clientSessionToken, backupSharesObj);
    assertCurrent();
    const userSharesString = JSON.stringify(userShares);
    storeUserShares(userSharesString);
    await notifyMpcWalletOnUserSharesStored(clientSessionToken, userShares.SECP256K1.id, userShares.ED25519.id);
    assertCurrent();
    console.log('[MPC] Waiting for server to process share registration...');
    await new Promise(r => setTimeout(r, 2000));
    assertCurrent();
    clearMpcProgress();
    console.log('[MPC] MPC wallet recovered successfully (resumed)');
    const mpcWalletData = {
        clientId,
        clientSessionToken,
        userSharesString,
        ethWalletAddress: ethWalletAddress,
        solanaWalletAddress: solanaWalletAddress,
        // Recovery capability is reconstruction input only, never route authority.
        authToken: null,
    };
    return mpcWalletData;
}
export async function fullLoginProcess(signMessageAsync, walletAddress, assertCurrent = () => { }) {
    assertCurrent();
    safeDebugLogs.length = 0;
    debugLog('[MPC] fullLoginProcess start');
    let mpcWalletData = null;
    const progress = loadMpcProgress();
    if (progress && progress.walletAddress === walletAddress.toLowerCase()) {
        debugLog('[MPC] Found saved progress');
        if (progress.step === 'decryption_complete') {
            debugLog('[MPC] Resuming from decryption_complete');
            mpcWalletData = await resumeFromDecryptionComplete(signMessageAsync, walletAddress, progress, assertCurrent);
            assertCurrent();
            debugLog('[MPC] MPC reconstruction done (resumed); requiring live-wallet refresh');
        }
    }
    if (!mpcWalletData) {
        mpcWalletData = await recoverMpcWalletProcess(signMessageAsync, walletAddress, assertCurrent);
        assertCurrent();
    }
    if (!mpcWalletData) {
        debugLog('[MPC] No recovery data, creating new wallet...');
        mpcWalletData = await createMpcWalletProcess(signMessageAsync, walletAddress, assertCurrent);
        assertCurrent();
    }
    // Even when MPC shares and a recovery credential are cached, prove control
    // of the currently live wallet to the backend again.  The signed
    // /mpc/refresh response may assist the subsequent MPC auth issuance, but
    // cached material alone can never complete login.
    if (!mpcWalletData.clientId || !mpcWalletData.userSharesString) {
        throw new Error('MPC reconstruction completed without required client data');
    }
    // Cached/recovered data is reconstruction input only. Authorization issuance
    // is sequenced strictly after a fresh signed /mpc/refresh for this live wallet.
    debugLog('[MPC] Refreshing client session with a fresh live-wallet signature...');
    mpcWalletData = await completeFreshWalletBoundAuthorization(mpcWalletData, async () => {
        const freshSession = await refreshClientSessionToken(signMessageAsync, walletAddress, mpcWalletData.clientId);
        return freshSession.clientSessionToken;
    }, (data, clientSessionToken) => {
        data.clientSessionToken = clientSessionToken;
        storeClientSessionToken(clientSessionToken);
    }, async (data) => {
        debugLog('[MPC] Requesting a fresh backend auth token for current wallet...');
        return createAuthTokenProcess(signMessageAsync, walletAddress, data, assertCurrent);
    }, assertCurrent);
    assertCurrent();
    storeMpcWalletData(mpcWalletData);
    console.log('[MPC] Full login process completed');
    return mpcWalletData;
}
